import { Form, Select, Typography, message as antdMessage } from 'antd';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useMemo } from 'react';
import { FiNavigation } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import DefaultButton from 'components/atoms/DefaultButton';
import DefaultCard from 'components/atoms/DefaultCard';
import HelmetWrapper from 'components/atoms/HelmetWrapper';
import PageHeaderPusher from 'components/atoms/PageHeaderPusher';
import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import DefaultPageHeader from 'components/composites/DefaultPageHeader';
import FarmbotAgriWebbIntegrationTitle from 'components/features/integrations/FarmbotAgriWebbIntegrationTitle';
import SensorIcon from 'components/icons/SensorIcon';
import { ErrorBoundary } from 'config/sentry';
import { AGRIWEBB_USED_AUTH_CODES_KEY } from 'constants/agriwebb';
import { activateAccount } from 'store/modules/agriWebb/actions';
import AgriWebbActionType from 'store/modules/agriWebb/constants';
import { makeSelectIsLoading } from 'store/modules/loading/selectors';
import { loadMyUser } from 'store/modules/myUser/actions';
import { makeSelectMyUserMembershipsEnterprisesByRoles } from 'store/modules/myUser/selectors';
import Enterprise from 'types/models/enterprise';
import { UserRole } from 'types/models/membership';
import getWhiteLabelConfig from 'utils/get-white-label-config';

import PermissionRequest from '../PermissionRequest';

import './styles.less';

const actions = [AgriWebbActionType.ACTIVATE_ACCOUNT_REQUEST];
const selectIsActivating = makeSelectIsLoading(actions);

const { title: whiteLabel } = getWhiteLabelConfig();

const getUsedAgriWebbAuthCodes = (): string[] => {
  const rawCodes = sessionStorage.getItem(AGRIWEBB_USED_AUTH_CODES_KEY);
  if (!rawCodes) {
    return [];
  }
  try {
    const parsedCodes = JSON.parse(rawCodes) as string[];
    return Array.isArray(parsedCodes) ? parsedCodes : [];
  } catch {
    return [];
  }
};

const markAgriWebbAuthCodeAsUsed = (authCode: string): void => {
  const usedCodes = getUsedAgriWebbAuthCodes();
  if (usedCodes.includes(authCode)) {
    return;
  }
  sessionStorage.setItem(
    AGRIWEBB_USED_AUTH_CODES_KEY,
    JSON.stringify([...usedCodes, authCode])
  );
};

export default function InstallCallbackAgriWebb() {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const authCode = searchParams.get('code') || '';
  const location = useLocation();
  const isCodeAlreadyUsed = useMemo(
    () => Boolean(authCode) && getUsedAgriWebbAuthCodes().includes(authCode),
    [authCode]
  );
  const isActivating = useSelector(selectIsActivating);
  const navigate = useNavigate();
  const myUserMembershipsEnterprises = useSelector(
    makeSelectMyUserMembershipsEnterprisesByRoles([
      UserRole.ADMIN,
      UserRole.SUPERVISOR
    ]),
    isEqual
  );

  const enterpriseOptions = useMemo(
    () =>
      myUserMembershipsEnterprises.map(({ name, id }) => ({
        label: name,
        value: id
      })),
    [myUserMembershipsEnterprises]
  );

  const goToSiteMappingPage = useCallback(
    (enterpriseId) => {
      dispatch(loadMyUser()); // To refresh the integration status
      navigate(
        `/connections/agriwebb/site-mapping?enterpriseId=${enterpriseId}`
      );
    },
    [dispatch, navigate]
  );

  const submit = useCallback(() => {
    if (!authCode || isCodeAlreadyUsed || isActivating) {
      return;
    }
    form
      .validateFields()
      .then(({ enterpriseId }) => {
        const enterprise: Enterprise | undefined =
          myUserMembershipsEnterprises.find((e) => e.id === enterpriseId);
        if (!enterprise || enterprise.length === 0) {
          throw new Error('No enterprise selected');
        }
        dispatch(
          activateAccount(
            {
              enterpriseId: enterprise.id,
              agriWebbCode: authCode,
              whiteLabel
            },
            () => {
              goToSiteMappingPage(enterpriseId);
            },
            (errorMessage: string) => antdMessage.error(
              errorMessage
              || 'Failed to activate the enterprise. Please restart the integration from AgriWebb.'
            )
          )
        );
        // Mark the code as used immediately to block double-submission from the same tab.
        // The backend has its own replay protection (AgriWebbActivationCodeCache); this is a
        // best-effort UX guard against accidental double-clicks and saga re-dispatch.
        markAgriWebbAuthCodeAsUsed(authCode);
        navigate(location.pathname, { replace: true });
      })
      .catch(() => { });
  }, [
    form,
    myUserMembershipsEnterprises,
    dispatch,
    authCode,
    isCodeAlreadyUsed,
    isActivating,
    goToSiteMappingPage,
    navigate,
    location.pathname
  ]);

  const missingAuthCodePageContent = (
    <Typography.Paragraph>
      Missing authorization code. Please restart the integration from AgriWebb.
    </Typography.Paragraph>
  );
  const usedAuthCodePageContent = (
    <Typography.Paragraph>
      This authorization code has already been used. Please restart the integration from AgriWebb.
    </Typography.Paragraph>
  );
  const validAuthCodePageContent = (
    <>
      <FarmbotAgriWebbIntegrationTitle>
        You&apos;ll be connecting the following data with AgriWebb.
      </FarmbotAgriWebbIntegrationTitle>
      <DefaultCard className="InstallCallback-card">
        <SensorIcon
          style={{
            position: 'relative',
            width: 24,
            top: 5,
            marginRight: 10
          }}
        />
        <Typography.Text strong>Sensor Data</Typography.Text>
        <Typography.Paragraph>
          Sensor data includes: recorded sensor data, sensor name and
          sensor configuration.
        </Typography.Paragraph>
      </DefaultCard>
      <DefaultCard className="InstallCallback-card">
        <FiNavigation
          style={{
            position: 'relative',
            width: 24,
            top: 2,
            marginRight: 10
          }}
        />
        <Typography.Text strong>Location</Typography.Text>
        <Typography.Paragraph>
          Location includes: Sensor location, Enterprise and Site
          location and time zones.
        </Typography.Paragraph>
      </DefaultCard>
      <Typography.Paragraph className="InstallCallback-text">
        <Typography.Text strong>Select enterprise</Typography.Text>
      </Typography.Paragraph>
      <Typography.Paragraph className="InstallCallback-text">
        Which enterprise would you like to grant AgriWebb-OAuth access
        to?
      </Typography.Paragraph>
      <DefaultCard className="InstallCallback-card">
        <DefaultForm
          form={form}
          layout="vertical"
          name="selectEnterprise"
          autoComplete="off"
          initialValues={{
            enterpriseId: enterpriseOptions?.[0]?.value
          }}
        >
          <DefaultFormItem name="enterpriseId" label="" required>
            <Select
              filterOption={false}
              options={enterpriseOptions}
              placeholder="Enterprise"
            />
          </DefaultFormItem>
          <div className="InstallCallback-footer">
            <DefaultButton
              className="InstallCallback-footer-button"
              onClick={submit}
              loading={isActivating}
              disabled={!authCode || isCodeAlreadyUsed || isActivating}
            >
              Allow
            </DefaultButton>
          </div>
        </DefaultForm>
      </DefaultCard>
    </>
  );

  return (
    <ErrorBoundary>
      <HelmetWrapper section="Connections" />
      <DefaultPageHeader title="Connections" />
      <PageHeaderPusher>
        <>
          {!myUserMembershipsEnterprises.length && <PermissionRequest />}
          {myUserMembershipsEnterprises.length > 0 && (
            <div className="InstallCallback">
              {!authCode
                ? missingAuthCodePageContent
                : isCodeAlreadyUsed
                  ? usedAuthCodePageContent
                  : validAuthCodePageContent}
            </div>
          )}
        </>
      </PageHeaderPusher>
    </ErrorBoundary>
  );
}
